import { useTranslation } from 'react-i18next'

import { DisbursementStatus } from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { Icon, Menu, type SectionItem, type SectionProps } from '@percent/lemonade'
import { DisbursementsDetailsActionsMenuProps } from './DisbursementsDetailsActionsMenu.types'
import style from './DisbursementsDetailsActionsMenu.module.scss'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'

export function DisbursementsDetailsActionsMenu({
  handleCancel,
  handleMarkAsApproved,
  handleMarkAsPaid,
  handleMarkAsSent,
  handleReturnPayment,
  handleConfirmBeneficiary,
  handleViewBankDetails,
  handleViewDonationsList,
  disbursement
}: DisbursementsDetailsActionsMenuProps) {
  const { t } = useTranslation()
  const { userHasPermissions } = usePermissions()
  const disbursementDonationsViewFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENT_DONATIONS_VIEW
  )
  const logReturnedPaymentFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_LOG_RETURNED_PAYMENT)
  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )
  const beneficiariesFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_BENEFICIARIES)
  const disbursementBankDetailsLinkFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_DISBURSEMENT_BANK_DETAILS_LINK
  )

  const canViewDonations = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('donations:can_list')
  const canLogReturnedPayment =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_mark_returned')
  const canApprove = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_approve')
  const canMarkAsPaid = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_mark_paid')
  const canMarkAsSent =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_mark_in_transit')
  const canCancel = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_cancel')
  const canConfirmBeneficiary =
    !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_confirm_beneficiary')

  const isDisbursementPending = [
    DisbursementStatus.PENDING_APPROVAL,
    DisbursementStatus.PENDING_TRUSTEE_APPROVAL,
    DisbursementStatus.PENDING_AGENT_APPROVAL,
    DisbursementStatus.PENDING_MANAGER_APPROVAL,
    DisbursementStatus.PENDING_PAYMENT
  ].includes(disbursement.status)

  const shouldShowDisbursementActions =
    disbursement.status === DisbursementStatus.PENDING_PAYMENT ||
    disbursement.status === DisbursementStatus.PENDING_APPROVAL ||
    disbursement.status === DisbursementStatus.SENT ||
    disbursement.status === DisbursementStatus.RETURNED

  const isMultiApprovalStatus =
    disbursement.status === DisbursementStatus.PENDING_TRUSTEE_APPROVAL ||
    disbursement.status === DisbursementStatus.PENDING_AGENT_APPROVAL ||
    disbursement.status === DisbursementStatus.PENDING_MANAGER_APPROVAL

  // A provider-failed payment must be removable so its donations roll into a later batch.
  const isPaymentFailed = disbursement.status === DisbursementStatus.FAILED

  const shouldShowCancelAction = shouldShowDisbursementActions || isMultiApprovalStatus || isPaymentFailed

  const primaryItems: SectionItem[] = [
    ...(disbursementDonationsViewFeatureFlag
      ? [
          {
            key: 'viewDonationsList',
            label: t('menu.viewDonationsList'),
            onClick: handleViewDonationsList,
            shouldBeDisabled: !canViewDonations
          }
        ]
      : []),
    ...(logReturnedPaymentFeatureFlag && handleReturnPayment
      ? [
          {
            key: 'returnPayment',
            label: t('menu.logAReturnedPayment'),
            onClick: handleReturnPayment,
            shouldBeDisabled: !canLogReturnedPayment
          }
        ]
      : []),
    ...(beneficiariesFeatureFlag && handleConfirmBeneficiary
      ? [
          {
            key: 'confirmBeneficiary',
            label: t('menu.confirmBeneficiary'),
            onClick: handleConfirmBeneficiary,
            shouldBeDisabled: !canConfirmBeneficiary || !disbursement.hasActivePaymentMethod || !isDisbursementPending,
            disabledTooltipInfo: !isDisbursementPending
              ? t('menu.confirmBeneficiaryNotPending')
              : !disbursement.hasActivePaymentMethod
              ? t('menu.confirmBeneficiaryNoPaymentMethod')
              : undefined
          }
        ]
      : [])
  ]

  const cancelItem: SectionItem | undefined = handleCancel
    ? {
        key: 'removeFromBatch',
        label: t('menu.removeFromBatch'),
        onClick: handleCancel,
        shouldBeDisabled: !canCancel
      }
    : undefined

  const markAsActionItems: SectionItem[] = [
    ...(handleMarkAsApproved
      ? [
          {
            key: 'markAsApproved',
            label: t('menu.markAsFoundationApproved'),
            onClick: handleMarkAsApproved,
            shouldBeDisabled: !canApprove
          }
        ]
      : []),
    ...(handleMarkAsPaid
      ? [
          {
            key: 'markAsPaid',
            label: t('menu.markAsPaid'),
            onClick: handleMarkAsPaid,
            shouldBeDisabled: !canMarkAsPaid
          }
        ]
      : []),
    ...(handleMarkAsSent
      ? [
          {
            key: 'markAsSent',
            label: t('menu.markAsSent'),
            onClick: handleMarkAsSent,
            dataTestId: 'mark-as-sent-action',
            shouldBeDisabled: !canMarkAsSent
          }
        ]
      : [])
  ]

  // Single row-actions section. Mark-as-* actions show only for standard statuses; cancel additionally shows
  // for multi-approval statuses. Cancel stays grouped with the mark-as-* items (no extra divider) via one path.
  const actionItems: SectionItem[] = [
    ...(shouldShowDisbursementActions ? markAsActionItems : []),
    ...(shouldShowCancelAction && cancelItem ? [cancelItem] : [])
  ]

  const bankDetailsItems: SectionItem[] = [
    ...(disbursementBankDetailsLinkFeatureFlag && handleViewBankDetails
      ? [
          {
            key: 'viewBankDetails',
            label: t('menu.bankDetails'),
            icon: 'external-link' as const,
            onClick: handleViewBankDetails
          }
        ]
      : [])
  ]

  const sections: SectionProps[] = [{ items: primaryItems }, { items: actionItems }, { items: bankDetailsItems }]

  return (
    <Menu
      sections={sections}
      customTriggerComponent={
        <span role="button" data-testid="actions-menu-button" className={style.trigger}>
          <Icon name="menu-vertical" size="s" color="neutral400" />
        </span>
      }
    />
  )
}
